/* ═══════════════════════════════════════════════════════════════
   LoginPage — Sign in, Create Firm, or Join Firm
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { Check, Eye, EyeOff } from 'lucide-react';
import { AuthService } from '../services/authService';
import type { AuthUser } from '../services/authService';
import { InvitationService } from '../services/invitationService';
import { Button } from '../components/ui/button';
import { ClerkSignInPanel, DesktopSignInPanel } from './ClerkSignIn';

interface Props {
  onAuth: (user: AuthUser) => void;
}

type Mode =
  | 'clerk'
  | 'login'
  | 'create-firm'
  | 'join-firm'
  | 'forgot'
  | 'reset-code'
  | 'redeem-invite';

// Auth is an on-web process (docs/AUTH_CLERK_PLAN.md): the browser gets Clerk's
// sign-in (password + Microsoft), the exe gets a browser-handoff panel. The
// legacy password/registration forms remain reachable (web) as fallback paths.
const IS_ELECTRON = typeof navigator !== 'undefined' && /electron/i.test(navigator.userAgent);

// Deterministic id for pairing a real <label> to its input via htmlFor/id.
function slugId(label: string) {
  return (
    'fld-' +
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );
}

export function LoginPage({ onAuth }: Props) {
  const [mode, setMode] = useState<Mode>('clerk');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [firmName, setFirmName] = useState('');
  const [firmCode, setFirmCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Redeem-invite state
  const [inviteCode, setInviteCode] = useState('');

  const handleRedeemInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!displayName.trim()) {
      setError('Enter your name');
      return;
    }
    if (!inviteCode.trim()) {
      setError('Enter your invite code from the email');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await InvitationService.redeem({
        email,
        token: inviteCode.trim(),
        password,
        displayName: displayName.trim(),
      });
      // Persist the JWT the same way AuthService does so the app sees us as signed in.
      (AuthService as any).setSession?.(res.token, res.user);
      try {
        localStorage.setItem('specforma.authToken', res.token);
      } catch {}
      try {
        localStorage.setItem('specforma.authUser', JSON.stringify(res.user));
      } catch {}
      if (res.user) onAuth(res.user);
    } catch (err: any) {
      setError(err.message || 'Invite redemption failed');
    }
    setLoading(false);
  };

  // Forgot/reset password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await AuthService.signIn(email, password);
    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }
    if (result.user) onAuth(result.user);
    setLoading(false);
  };

  const handleCreateFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!displayName.trim()) {
      setError('Enter your name');
      return;
    }
    if (!firmName.trim()) {
      setError('Enter your company name');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);

    const result = await AuthService.signUpAndCreateFirm(
      email,
      password,
      displayName.trim(),
      firmName.trim(),
      ''
    );
    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }
    if (result.user) onAuth(result.user);
    else setSignupSuccess(true);
    setLoading(false);
  };

  const handleJoinFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!displayName.trim()) {
      setError('Enter your name');
      return;
    }
    if (!firmCode.trim()) {
      setError('Enter the company code from your admin');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);

    const result = await AuthService.signUpAndJoinFirm(
      email,
      password,
      displayName.trim(),
      firmCode.trim()
    );
    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }
    if (result.user) onAuth(result.user);
    else setSignupSuccess(true);
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await AuthService.forgotPassword(resetEmail);
    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }
    setMode('reset-code');
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    const result = await AuthService.resetPassword(resetEmail, resetCode, newPassword);
    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }
    setResetSuccess(true);
    setLoading(false);
  };

  if (resetSuccess) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>SpecForma</div>
          <div style={{ textAlign: 'center', padding: 'var(--space-5) 0' }}>
            <div
              style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-3)' }}
            >
              <Check size={40} strokeWidth={2} style={{ color: 'var(--ok)' }} aria-hidden="true" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 'var(--space-2)' }}>
              Password Reset
            </div>
            <div style={{ fontSize: 13, color: 'var(--tm)', lineHeight: 1.5 }}>
              Your password has been reset. You can now sign in with your new password.
            </div>
            <Button
              className="w-full"
              style={{ marginTop: 'var(--space-5)' }}
              onClick={() => {
                setResetSuccess(false);
                setMode('login');
                setError('');
                setResetCode('');
                setNewPassword('');
                setConfirmNewPassword('');
              }}
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (signupSuccess) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>SpecForma</div>
          <div style={{ textAlign: 'center', padding: 'var(--space-5) 0' }}>
            <div
              style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-3)' }}
            >
              <Check size={40} strokeWidth={2} style={{ color: 'var(--ok)' }} aria-hidden="true" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 'var(--space-2)' }}>
              Account Created
            </div>
            <div style={{ fontSize: 13, color: 'var(--tm)', lineHeight: 1.5 }}>
              Your account is ready. Sign in to get started.
            </div>
            <Button
              className="w-full"
              style={{ marginTop: 'var(--space-5)' }}
              onClick={() => {
                setSignupSuccess(false);
                setMode('login');
              }}
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>SpecForma</div>

        {/* ── Clerk sign-in (default): web = Clerk UI, exe = browser handoff ── */}
        {mode === 'clerk' && (
          <>
            {IS_ELECTRON ? (
              <DesktopSignInPanel onAuth={onAuth} />
            ) : (
              <>
                <ClerkSignInPanel onAuth={onAuth} />
                <div style={styles.divider}>
                  <span style={styles.dividerText}>New to SpecForma?</span>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setMode('create-firm');
                      setError('');
                    }}
                  >
                    Register your company
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setMode('join-firm');
                      setError('');
                    }}
                  >
                    Join your team
                  </Button>
                  <Button
                    variant="outline"
                    style={{ flex: '1 1 100%' }}
                    onClick={() => {
                      setMode('redeem-invite');
                      setError('');
                    }}
                  >
                    Redeem invite
                  </Button>
                </div>
                <div style={{ textAlign: 'center', marginTop: 'var(--space-3)' }}>
                  <LinkButton
                    onClick={() => {
                      setMode('login');
                      setError('');
                    }}
                  >
                    Classic sign-in
                  </LinkButton>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Sign In ── */}
        {mode === 'login' && (
          <>
            <div style={styles.subtitle}>Sign in to your account</div>
            <form onSubmit={handleLogin} style={styles.form}>
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
              <PasswordField
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
              {error && (
                <div style={styles.error} role="alert">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? '...' : 'Sign In'}
              </Button>
            </form>
            <div style={{ textAlign: 'center', marginTop: 'var(--space-3)' }}>
              <LinkButton
                onClick={() => {
                  setMode('forgot');
                  setError('');
                  setResetEmail(email);
                }}
              >
                Forgot password?
              </LinkButton>
            </div>
            <div style={styles.divider}>
              <span style={styles.dividerText}>New to SpecForma?</span>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setMode('create-firm');
                  setError('');
                }}
              >
                Register your company
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setMode('join-firm');
                  setError('');
                }}
              >
                Join your team
              </Button>
              <Button
                variant="outline"
                style={{ flex: '1 1 100%' }}
                onClick={() => {
                  setMode('redeem-invite');
                  setError('');
                }}
              >
                Redeem invite
              </Button>
            </div>
          </>
        )}

        {/* ── Redeem Invite ── */}
        {mode === 'redeem-invite' && (
          <>
            <div style={styles.subtitle}>Redeem your SpecForma invite</div>
            <form onSubmit={handleRedeemInvite} style={styles.form}>
              <div style={{ fontSize: 13, color: 'var(--tm)', lineHeight: 1.5 }}>
                Paste the invite code from your email, then create your password.
              </div>
              <Field
                label="Your Name"
                value={displayName}
                onChange={setDisplayName}
                placeholder="Your full name"
                autoComplete="name"
                required
              />
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="The email that received the invite"
                autoComplete="email"
                required
              />
              <div>
                <label htmlFor={slugId('Invite Code')} style={styles.label}>
                  Invite Code
                </label>
                <input
                  id={slugId('Invite Code')}
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.trim())}
                  placeholder="Paste the code from the email"
                  style={{
                    ...styles.input,
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '0.02em',
                  }}
                  required
                />
              </div>
              <PasswordField
                label="Create Password"
                value={password}
                onChange={setPassword}
                placeholder="Min 6 characters"
                autoComplete="new-password"
                required
                minLength={6}
              />
              <PasswordField
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Re-enter password"
                autoComplete="new-password"
                required
                minLength={6}
              />
              {error && (
                <div style={styles.error} role="alert">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? '...' : 'Redeem & Sign In'}
              </Button>
            </form>
            <div style={styles.toggle}>
              <LinkButton
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                Back to sign in
              </LinkButton>
            </div>
          </>
        )}

        {/* ── Forgot Password ── */}
        {mode === 'forgot' && (
          <>
            <div style={styles.subtitle}>Reset your password</div>
            <form onSubmit={handleForgotPassword} style={styles.form}>
              <div style={{ fontSize: 13, color: 'var(--tm)', lineHeight: 1.5 }}>
                Enter your email and we'll send a 6-digit code to reset your password.
              </div>
              <Field
                label="Email"
                type="email"
                value={resetEmail}
                onChange={setResetEmail}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
              {error && (
                <div style={styles.error} role="alert">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading || !resetEmail} className="w-full">
                {loading ? '...' : 'Send Reset Code'}
              </Button>
            </form>
            <div style={styles.toggle}>
              <LinkButton
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                Back to sign in
              </LinkButton>
            </div>
          </>
        )}

        {/* ── Enter Reset Code ── */}
        {mode === 'reset-code' && (
          <>
            <div style={styles.subtitle}>Enter your reset code</div>
            <form onSubmit={handleResetPassword} style={styles.form}>
              <div style={{ fontSize: 13, color: 'var(--tm)', lineHeight: 1.5 }}>
                We sent a 6-digit code to <b>{resetEmail}</b>. Enter it below with your new
                password.
              </div>
              <div>
                <label htmlFor={slugId('6-Digit Code')} style={styles.label}>
                  6-Digit Code
                </label>
                <input
                  id={slugId('6-Digit Code')}
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{
                    ...styles.input,
                    textAlign: 'center',
                    letterSpacing: '0.3em',
                    fontSize: 18,
                    fontWeight: 700,
                    fontFamily: 'var(--font-mono)',
                  }}
                  required
                  maxLength={6}
                />
              </div>
              <PasswordField
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="Min 6 characters"
                autoComplete="new-password"
                required
                minLength={6}
              />
              <PasswordField
                label="Confirm Password"
                value={confirmNewPassword}
                onChange={setConfirmNewPassword}
                placeholder="Re-enter password"
                autoComplete="new-password"
                required
                minLength={6}
              />
              {error && (
                <div style={styles.error} role="alert">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading || resetCode.length !== 6} className="w-full">
                {loading ? '...' : 'Reset Password'}
              </Button>
            </form>
            <div style={styles.toggle}>
              <LinkButton
                onClick={() => {
                  setMode('forgot');
                  setError('');
                }}
              >
                Resend code
              </LinkButton>
              {' · '}
              <LinkButton
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                Back to sign in
              </LinkButton>
            </div>
          </>
        )}

        {/* ── Create Firm ── */}
        {mode === 'create-firm' && (
          <>
            <div style={styles.subtitle}>Register your company on SpecForma</div>
            <form onSubmit={handleCreateFirm} style={styles.form}>
              <Field
                label="Company Name"
                value={firmName}
                onChange={setFirmName}
                placeholder="Acme Engineering"
                required
              />
              <div style={{ borderTop: '1px solid var(--bd)', margin: 'var(--space-1) 0' }} />
              <Field
                label="Your Name"
                value={displayName}
                onChange={setDisplayName}
                placeholder="Josh Beardsley"
                autoComplete="name"
                required
              />
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
              <PasswordField
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="Min 6 characters"
                autoComplete="new-password"
                required
                minLength={6}
              />
              <PasswordField
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Re-enter password"
                autoComplete="new-password"
                required
                minLength={6}
              />
              {error && (
                <div style={styles.error} role="alert">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? '...' : 'Create Company Account'}
              </Button>
            </form>
            <div style={styles.toggle}>
              <LinkButton
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                Back to sign in
              </LinkButton>
            </div>
          </>
        )}

        {/* ── Join Firm ── */}
        {mode === 'join-firm' && (
          <>
            <div style={styles.subtitle}>Join your team on SpecForma</div>
            <form onSubmit={handleJoinFirm} style={styles.form}>
              <div>
                <label htmlFor={slugId('Company Code')} style={styles.label}>
                  Company Code
                </label>
                <input
                  id={slugId('Company Code')}
                  value={firmCode}
                  onChange={(e) => setFirmCode(e.target.value.toLowerCase())}
                  placeholder="e.g. acme-eng"
                  style={styles.input}
                  required
                />
                <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 4 }}>
                  Get this from your company admin
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--bd)', margin: 'var(--space-1) 0' }} />
              <Field
                label="Your Name"
                value={displayName}
                onChange={setDisplayName}
                placeholder="Your full name"
                autoComplete="name"
                required
              />
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
              <PasswordField
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="Min 6 characters"
                autoComplete="new-password"
                required
                minLength={6}
              />
              <PasswordField
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Re-enter password"
                autoComplete="new-password"
                required
                minLength={6}
              />
              {error && (
                <div style={styles.error} role="alert">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? '...' : 'Join Team'}
              </Button>
            </form>
            <div style={styles.toggle}>
              <LinkButton
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                Back to sign in
              </LinkButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Text-link styled as a real, keyboard-focusable button (was a bare <span onClick>) ─── */
function LinkButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={styles.link}>
      {children}
    </button>
  );
}

/* ─── Field helper ─── */
function Field({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  minLength,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
}) {
  const id = slugId(label);
  return (
    <div>
      <label htmlFor={id} style={styles.label}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={styles.input}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
      />
    </div>
  );
}

/* ─── Password field with eye toggle ─── */
function PasswordField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  minLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
}) {
  const [visible, setVisible] = useState(false);
  const id = slugId(label);
  return (
    <div>
      <label htmlFor={id} style={styles.label}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ ...styles.input, paddingRight: 38 }}
          autoComplete={autoComplete}
          required={required}
          minLength={minLength}
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--tm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {visible ? (
            <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
          ) : (
            <Eye size={16} strokeWidth={2} aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    fontFamily: 'var(--font-sans)',
  },
  card: {
    width: 400,
    maxWidth: '92%',
    padding: 'var(--space-6)',
    background: 'var(--sf)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--bd)',
    boxShadow: 'var(--shadow-lg)',
  },
  logo: {
    fontSize: 22,
    fontWeight: 700,
    textAlign: 'center' as const,
    letterSpacing: '-0.02em',
    color: 'var(--tx)',
  },
  subtitle: { fontSize: 13, color: 'var(--tm)', textAlign: 'center' as const, marginTop: 4 },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-3)',
    marginTop: 'var(--space-5)',
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--tm)',
    marginBottom: 4,
    textTransform: 'uppercase' as const,
    letterSpacing: '.04em',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--bd)',
    background: 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    outline: 'none',
  },
  error: {
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    background: 'color-mix(in srgb, var(--dg) 10%, transparent)',
    color: 'var(--dg)',
    fontSize: 13,
    fontWeight: 500,
  },
  divider: {
    textAlign: 'center' as const,
    margin: 'var(--space-4) 0',
    borderTop: '1px solid var(--bd)',
    position: 'relative' as const,
  },
  dividerText: {
    background: 'var(--sf)',
    padding: '0 var(--space-3)',
    fontSize: 11,
    color: 'var(--tm)',
    position: 'relative' as const,
    top: -8,
  },
  toggle: {
    textAlign: 'center' as const,
    marginTop: 'var(--space-4)',
    fontSize: 13,
    color: 'var(--tm)',
  },
  link: {
    font: 'inherit',
    color: 'var(--ac)',
    cursor: 'pointer',
    fontWeight: 600,
    background: 'none',
    border: 'none',
    padding: 0,
    display: 'inline',
  },
};
